import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	FormGroup,
	FormControlLabel,
	Switch,
	Box,
	Button as MuiButton,
	Select,
	MenuItem,
} from "@mui/material";
import { filterFieldProps } from "./MaterialTableStyles";

/**
 * DynamicMaterialTable - Componente de tabela com suporte a larguras em porcentagem/pixels e responsividade automática
 *
 * LARGURAS EM PORCENTAGEM:
 * Para usar larguras em porcentagem nas colunas, adicione as seguintes propriedades ao objeto da coluna:
 * - widthPercentage: número (ex: 25 para 25% da largura total)
 * - minWidthPercentage: número (largura mínima em %)
 * - maxWidthPercentage: número (largura máxima em %)
 *
 * LARGURAS EM PIXELS:
 * Para usar larguras em pixels nas colunas, adicione as seguintes propriedades ao objeto da coluna:
 * - widthPixels: número (ex: 200 para 200px)
 * - minWidthPixels: número (largura mínima em pixels)
 * - maxWidthPixels: número (largura máxima em pixels)
 *
 * RESPONSIVIDADE AUTOMÁTICA:
 * A tabela se ajusta automaticamente baseada no tamanho da tela:
 * - Mobile (< 768px): Base de 600px
 * - Tablet (768px - 1024px): Base de 800px
 * - Laptop pequeno (1024px - 1366px): Base de 1000px
 * - Laptop médio (1366px - 1500px): Base de 1100px
 * - Desktop (1500px - 1920px): Base de 1200px
 * - Telas grandes (> 1920px): Base de 1400px
 *
 * Exemplo de uso com porcentagem:
 * const columns = [
 *   {
 *     accessorKey: 'name',
 *     header: 'Nome',
 *     widthPercentage: 30, // 30% da largura total (se adapta automaticamente)
 *     minWidthPercentage: 20, // mínimo 20%
 *   },
 *   {
 *     accessorKey: 'email',
 *     header: 'Email',
 *     widthPercentage: 40, // 40% da largura total
 *   },
 *   {
 *     accessorKey: 'actions',
 *     header: 'Ações',
 *     // Sem widthPercentage - será calculado automaticamente (30% restante)
 *   }
 * ];
 *
 * Exemplo de uso com pixels:
 * const columns = [
 *   {
 *     accessorKey: 'name',
 *     header: 'Nome',
 *     widthPixels: 200, // 200px fixos
 *     minWidthPixels: 150, // mínimo 150px
 *     maxWidthPixels: 300, // máximo 300px
 *   },
 *   {
 *     accessorKey: 'description',
 *     header: 'Descrição',
 *     widthPixels: 400, // 400px, pode crescer livremente se não tiver max
 *     minWidthPixels: 200, // mínimo 200px
 *   }
 * ];
 */

const DynamicMaterialTable = ({
	columns,
	data,
	initialState,
	setDataInTable,
	onFullScreenChange,
	renderTopToolbarCustomActions,
}) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [visibleColumns, setVisibleColumns] = useState({});
	const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
	const [facetedFilters, setFacetedFilters] = useState({});
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);
	const buttonRef = useRef(null);

	// Hook para detectar mudanças no tamanho da tela
	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth);
		};

		// Detectar mudanças de zoom também
		const handleZoom = () => {
			setScreenWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);
		window.addEventListener("zoom", handleZoom);
		
		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("zoom", handleZoom);
		};
	}, []);

	// Função para calcular a base de pixels baseada no tamanho da tela
	const getBaseWidth = () => {
		// Usar uma base menor para evitar overflow com zoom
		const availableWidth = window.innerWidth * 0.9; // 90% da largura da tela
		if (screenWidth < 768) return Math.min(availableWidth, 500);
		if (screenWidth < 1024) return Math.min(availableWidth, 700);
		if (screenWidth < 1366) return Math.min(availableWidth, 900);
		if (screenWidth < 1500) return Math.min(availableWidth, 1000);
		if (screenWidth < 1920) return Math.min(availableWidth, 1100);
		return Math.min(availableWidth, 1200);
	};

	const initalStateDefault = {
		showColumnFilters: true,
		columnVisibility: {},
	};

	if (!initialState) {
		initialState = { ...initalStateDefault };
	} else {
		initialState = { ...initialState, ...initalStateDefault };
	}

	// Função para calcular larguras automáticas se não especificadas
	const calculateColumnWidths = (columns) => {
		const columnsWithPercentage = columns.filter(
			(col) => col.widthPercentage,
		);
		const columnsWithoutPercentage = columns.filter(
			(col) => !col.widthPercentage,
		);

		if (
			columnsWithoutPercentage.length > 0 &&
			columnsWithPercentage.length > 0
		) {
			const usedPercentage = columnsWithPercentage.reduce(
				(sum, col) => sum + col.widthPercentage,
				0,
			);
			const remainingPercentage = 100 - usedPercentage;
			const autoWidth =
				remainingPercentage / columnsWithoutPercentage.length;

			return columns.map((col) => ({
				...col,
				widthPercentage: col.widthPercentage || autoWidth,
			}));
		}

		return columns;
	};

	// Aplicar cálculo automático se necessário
	const columnsWithCalculatedWidths = calculateColumnWidths(columns);

	// Adicionando faceted values às colunas processadas
	const updatedColumns = columnsWithCalculatedWidths.map((col) => {
		const baseWidth = getBaseWidth(); // Usar base dinâmica e flexível
		
		// Suporte para larguras em pixels
		if (col.widthPixels || col.minWidthPixels || col.maxWidthPixels) {
			
			return {
				...col,
				enableFacetedValues: true,
				// Usar valores diretos em pixels
				size: col.widthPixels || col.size || undefined,
				minSize: col.minWidthPixels || col.minSize || undefined,
				maxSize: col.maxWidthPixels || col.maxSize || undefined,
			};
		}
		
		// Suporte para larguras em porcentagem (funcionalidade existente)
		const convertedSize = col.widthPercentage
			? Math.round((col.widthPercentage / 100) * baseWidth)
			: col.size || undefined;


		return {
			...col,
			enableFacetedValues: true,
			// Usar as propriedades nativas da MaterialReactTable
			size: convertedSize,
			minSize: col.minWidthPercentage
				? Math.round((col.minWidthPercentage / 100) * baseWidth)
				: col.minSize || undefined,
			maxSize: col.maxWidthPercentage
				? Math.round((col.maxWidthPercentage / 100) * baseWidth)
				: col.maxSize || undefined,
		};
	});

	const table = useMaterialReactTable({
		enableFacetedValues: true,
		// enableColumnFilterModes: true,
		columnFilterDisplayMode: "subheader",
		columns: updatedColumns,
		data,
		initialState: initialState,
		localization: MRT_Localization_PT_BR,
		enablePagination: false,
		enableColumnVisibility: true,
		enableHiding: true,

		// enableTopToolbar: false,
		enableDensityToggle: false,
		enableGlobalFilter: false,

		enableColumnActions: false,
		enableSorting: false,

		enableBottomToolbar: false,

		muiTopToolbarProps: {
			sx: {
				backgroundColor: "#f5f5f5",
				borderBottom: "1px solid #ddd",
				padding: "0px", // reduced from 8px
				minHeight: "40px", // reduced from 48px
				display: "flex",
				justifyContent: "flex-end",
				"& .MuiButtonBase-root": {
					marginLeft: "0px", // reduced from 8px
					color: "#333333",
					padding: "0px", // added to reduce button size
					minWidth: "32px", // added to ensure minimal width
					"& .MuiButton-startIcon, & .MuiButton-endIcon": {
						margin: 0, // removes icon margins
					},
				},
			},
		},
		muiFilterDatePickerProps: {
			format: "DD/MM/YYYY",
		},
		muiTableProps: {
			sx: {
				width: "100%",
				tableLayout: "fixed",
				maxWidth: "100%",
				"& .MuiTable-root": {
					width: "100%",
					tableLayout: "fixed",
				},
				"& .MuiTableCell-root": {
					padding: "4px 8px", // Reduzir padding para economizar espaço
					wordBreak: "break-word", // Quebra palavras longas
					overflowWrap: "break-word", // Compatibilidade adicional
					whiteSpace: "normal", // Permite quebra de linha
					verticalAlign: "top", // Alinha conteúdo no topo da célula
				},
				"& .MuiTableHead-root .MuiTableCell-root": {
					padding: "2px 4px", // Padding ainda menor para cabeçalhos
					whiteSpace: "normal", // Permite quebra de linha nos cabeçalhos também
					wordBreak: "break-word",
				},
				"@media (max-width: 768px)": {
					fontSize: "0.8rem",
				},
			},
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				backgroundColor:
					row.original.periodic == 1
						? "#f1b44c"
						: row.index % 2 === 0
						? "#F9F9F9"
						: "#fff",
				"& .MuiTableCell-root": {
					height: "auto", // Altura automática baseada no conteúdo
					minHeight: "32px", // Altura mínima para manter consistência
				},
			},
		}),
		muiTableHeadProps: {
			sx: {
				"& .MuiTableRow-root": {
					height: "auto", // Altura automática para cabeçalhos
					minHeight: "30px", // Altura mínima
				},
				"& .MuiTableCell-root": {
					height: "auto",
					minHeight: "30px",
				},
			},
		},
		muiFilterTextFieldProps: filterFieldProps,

		renderColumnFilterModeMenuItems: ({ column }) => {
			const uniqueValues = Array.from(
				column.getFacetedUniqueValues().keys(),
			);

			return uniqueValues.length > 0 ? (
				<Select
					value={facetedFilters[column.id] || ""}
					onChange={(e) => {
						setFacetedFilters({
							...facetedFilters,
							[column.id]: e.target.value,
						});
						column.setFilterValue(e.target.value);
					}}
					displayEmpty
					fullWidth
				>
					<MenuItem value="">Todos</MenuItem>
					{uniqueValues.map((value) => (
						<MenuItem key={value} value={value}>
							{value}
						</MenuItem>
					))}
				</Select>
			) : null;
		},
		renderTopToolbarCustomActions: renderTopToolbarCustomActions,
		// renderTopToolbarCustomActions: () => {
		// 	return (
		// 		<Box
		// 			sx={{
		// 				display: "flex",
		// 				justifyContent: "flex-end",
		// 				alignItems: "center",
		// 				width: "100%",
		// 				marginTop: "7.5px",
		// 				marginRight: "-22px",
		// 			}}
		// 		>
		// 			<MuiButton
		// 				ref={buttonRef}
		// 				onClick={handleOpenModal}
		// 				style={{
		// 					background: "none",
		// 					border: "none",
		// 					boxShadow: "none",
		// 					padding: 0,
		// 				}}
		// 			>
		// 				<i
		// 					className="bx bx-columns"
		// 					style={{
		// 						color: "#757575",
		// 						fontSize: "24px",
		// 					}}
		// 				/>
		// 			</MuiButton>
		// 		</Box>
		// 	);
		// },
	});

	useEffect(() => {
		const fullScreenState = table.getState().isFullScreen;
		if (onFullScreenChange) {
			onFullScreenChange(fullScreenState);
		}
	}, [table.getState().isFullScreen, onFullScreenChange]);

	const toggleColumnVisibility = (columnId) => {
		const column = table.getColumn(columnId);
		if (column) {
			setVisibleColumns((prev) => ({
				...prev,
				[columnId]: !prev[columnId],
			}));
			column.toggleVisibility();
		} else {
			console.error(`Column with ID ${columnId} not found`);
		}
	};

	const handleShowAll = () => {
		columns.forEach((col) => {
			if (visibleColumns[col.accessorKey]) {
				setVisibleColumns((prev) => ({
					...prev,
					[col.accessorKey]: false,
				}));
			}
			table.getColumn(col.accessorKey).toggleVisibility(true);
		});
	};

	const handleHideAll = () => {
		columns.forEach((col) => {
			if (!visibleColumns[col.accessorKey]) {
				setVisibleColumns((prev) => ({
					...prev,
					[col.accessorKey]: true,
				}));
			}
			table.getColumn(col.accessorKey).toggleVisibility(false);
		});
	};

	const allColumnsVisible = columns.every(
		(col) => !visibleColumns[col.accessorKey],
	);
	const allColumnsHidden = columns.every(
		(col) => visibleColumns[col.accessorKey],
	);

	useEffect(() => {
		const filteredData = table
			.getFilteredRowModel()
			.rows.map((row) => row.original);
		if (setDataInTable) {
			setDataInTable(filteredData);
		}
	}, [setDataInTable, table.getState().columnFilters, data]);

	// const handleOpenModal = () => {
	// 	if (buttonRef.current) {
	// 		const buttonRect = buttonRef.current.getBoundingClientRect();
	// 		setModalPosition({ top: buttonRect.bottom, left: buttonRect.left });
	// 		setModalOpen(true);
	// 	}
	// };

	return (
		<>
			<style>
				{`
                    .MuiIconButton-root[aria-label="Mostrar/Ocultar colunas"] {
                        display: none !important;
                    }
                `}
			</style>
			<div style={{ 
				width: "100%", 
				maxWidth: "100%"
			}}>
				<MaterialReactTable table={table} />
			</div>
			<Dialog
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				PaperProps={{
					style: {
						position: "absolute",
						top: `${modalPosition.top - 100}px`,
						left: `${modalPosition.left - 200}px`,
					},
				}}
				BackdropProps={{
					style: {
						backgroundColor: "transparent",
					},
				}}
			>
				<DialogTitle>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>
							<MuiButton
								onClick={handleShowAll}
								disabled={allColumnsVisible}
								style={{
									color: allColumnsVisible ? "gray" : "blue",
									background: "none",
									border: "none",
									marginRight: "8px",
								}}
							>
								Mostrar Tudo
							</MuiButton>
							<MuiButton
								onClick={handleHideAll}
								disabled={allColumnsHidden}
								style={{
									color: allColumnsHidden ? "gray" : "blue",
									background: "none",
									border: "none",
								}}
							>
								Ocultar Tudo
							</MuiButton>
						</div>
					</div>
				</DialogTitle>
				<DialogContent>
					{columns.map((col) => (
						<FormGroup key={col.accessorKey}>
							<FormControlLabel
								control={
									<Switch
										checked={
											!visibleColumns[col.accessorKey]
										}
										onClick={() =>
											toggleColumnVisibility(
												col.accessorKey,
											)
										}
									/>
								}
								label={col.header}
							/>
						</FormGroup>
					))}
				</DialogContent>
			</Dialog>
		</>
	);
};

DynamicMaterialTable.propTypes = {
	columns: PropTypes.array.isRequired,
	data: PropTypes.array.isRequired,
	initialState: PropTypes.object,
	setDataInTable: PropTypes.func,
	onFullScreenChange: PropTypes.func,
	renderTopToolbarCustomActions: PropTypes.func,
};

export default DynamicMaterialTable;
